// Combo — one searchable pick-one dropdown for the directory pages
// (listPage.css). A button shows the choice; opening it gives a search box
// over the list: type to narrow, ↑/↓ to move, Enter to pick, Esc to close
// (focus goes back to the button). The panel is PORTALLED and fixed to the
// viewport so a table's overflow can never clip it (see Sheet.jsx on why a
// z-index alone cannot), and it opens upward when there is no room below.
//
//   options  [{ value, label }]  — value '' is a legitimate choice
//   variant  'field' (a toolbar control) | 'cell' (reads as text in a row)

import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const PANEL_MAX = 300; // px — the list's tallest, search box included

function Chevron() {
  return (
    <svg className="cb-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}
function Tick() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

export default function Combo({
  value, options, onChange, variant = 'field', placeholder = 'Select…', searchPlaceholder = 'Search…',
  disabled = false, ariaLabel, minWidth = 240, className = '',
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const listRef = useRef(null);
  const listId = useId();

  const current = options.find((o) => o.value === value);
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;
  }, [options, q]);

  const close = (refocus = true) => {
    setOpen(false); setQ('');
    if (refocus) btnRef.current?.focus();
  };
  const pick = (o) => { close(); if (o && o.value !== value) onChange(o.value); };
  const openPanel = () => {
    if (disabled) return;
    setQ('');
    setActive(Math.max(0, options.findIndex((o) => o.value === value)));
    setOpen(true);
  };

  // Where the panel goes: under the button, or over it when the viewport
  // has no room below; clamped to the viewport's sides.
  useLayoutEffect(() => {
    if (!open) return;
    const r = btnRef.current.getBoundingClientRect();
    const width = Math.max(r.width, minWidth);
    const left = Math.min(Math.max(8, r.left), window.innerWidth - width - 8);
    const below = window.innerHeight - r.bottom;
    const up = below < PANEL_MAX + 12 && r.top > below;
    setPos(up ? { left, width, bottom: window.innerHeight - r.top + 4 } : { left, width, top: r.bottom + 4 });
  }, [open, minWidth]);

  // A click elsewhere, a scroll of the page under it, or a resize closes it.
  useEffect(() => {
    if (!open) return undefined;
    const inside = (t) => panelRef.current?.contains(t) || btnRef.current?.contains(t);
    const onDown = (e) => { if (!inside(e.target)) close(false); };
    const onScroll = (e) => { if (!panelRef.current?.contains(e.target)) close(false); };
    const onResize = () => close(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the highlighted option in view as the arrows move it.
  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector(`[data-i="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [open, active, shown]);

  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(shown.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Home') { e.preventDefault(); setActive(0); }
    else if (e.key === 'End') { e.preventDefault(); setActive(shown.length - 1); }
    else if (e.key === 'Enter') { e.preventDefault(); if (shown[active]) pick(shown[active]); }
    else if (e.key === 'Escape') { e.preventDefault(); close(); }
    else if (e.key === 'Tab') close(false);
  };

  return (
    <>
      <button
        ref={btnRef} type="button" disabled={disabled}
        className={`cb-trigger cb-${variant}${open ? ' cb-open' : ''}${!current ? ' cb-empty' : ''} ${className}`}
        aria-haspopup="listbox" aria-expanded={open} aria-label={ariaLabel}
        onClick={() => (open ? close() : openPanel())}
        onKeyDown={(e) => { if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) { e.preventDefault(); openPanel(); } }}>
        <span className="cb-label">{current ? current.label : placeholder}</span>
        <Chevron />
      </button>
      {open && pos && createPortal(
        <div ref={panelRef} className="cb-panel" style={pos}>
          <div className="cb-search">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
            </svg>
            <input
              autoFocus value={q} placeholder={searchPlaceholder}
              role="combobox" aria-expanded="true" aria-controls={listId} aria-autocomplete="list"
              aria-activedescendant={shown[active] ? `${listId}-${active}` : undefined}
              onChange={(e) => { setQ(e.target.value); setActive(0); }}
              onKeyDown={onKey} />
          </div>
          <ul ref={listRef} id={listId} role="listbox" className="cb-list" aria-label={ariaLabel}>
            {shown.map((o, i) => (
              <li
                key={o.value} id={`${listId}-${i}`} data-i={i} role="option" aria-selected={o.value === value}
                className={`cb-option${i === active ? ' cb-active' : ''}${o.value === value ? ' cb-selected' : ''}`}
                onMouseMove={() => { if (i !== active) setActive(i); }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(o)}>
                <span className="cb-option-label">{o.label}</span>
                {o.value === value && <Tick />}
              </li>
            ))}
            {!shown.length && <li className="cb-none">No matches</li>}
          </ul>
        </div>,
        document.body,
      )}
    </>
  );
}
